import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench } from "lucide-react";

export default function Signup() {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setSubmitting(true);
    setError("");
    const res = await signup(form);
    setSubmitting(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-b from-slate-50 to-blue-50">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-11 w-11 rounded-xl bg-blue-600 flex items-center justify-center">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ</span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <h1 className="text-2xl font-bold mb-1.5">Start your shop</h1>
          <p className="text-slate-500 text-sm mb-6">Create your company in 30 seconds. No credit card.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Your full name</Label>
              <Input required value={form.full_name} onChange={set("full_name")} className="h-12 mt-1.5 text-base" data-testid="signup-name" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Company name</Label>
              <Input required value={form.company_name} onChange={set("company_name")} className="h-12 mt-1.5 text-base" placeholder="e.g. Joe's Plumbing" data-testid="signup-company" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} className="h-12 mt-1.5 text-base" data-testid="signup-email" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Password</Label>
              <Input required type="password" value={form.password} onChange={set("password")} className="h-12 mt-1.5 text-base" data-testid="signup-password" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="signup-error">{error}</div>}
            <Button type="submit" disabled={submitting} className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700" data-testid="signup-submit">
              {submitting ? "Creating…" : "Create company & continue"}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-blue-600 hover:underline" data-testid="signup-to-login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
